<template>
  <b-dropdown
    id="customerselect_web_dropdown_button"
    block
    :text="selected.name"
    :size="size"
    class="dropdown--dropselect form-control"
    :class="toggleVariantClass"
    variant="dropselect"
    ref="customerdropdown"
  >
    <b-dropdown-form v-if="searchable" class="dropdown-search">
      <b-form-input
        v-model="filters.search_by_name"
        :placeholder="$gettext('Search by name...')"
        class="form-control"
        @input="debouncedSearch"
      />
      <font-awesome-icon
        v-if="filters.search_by_name != ''"
        @click.stop="clearSearch"
        icon="fa-solid fa-circle-xmark"
        class="reset-filter"
      />
    </b-dropdown-form>
    <GekkoButton
      v-if="searchable && allowAddBtn"
      @click="
        () => {
          $emit('add-customer', '')
        }
      "
      :text="$gettext('Add new customer')"
      icon="fa-solid fa-plus"
      variant="link"
      size="sm"
    />
    <b-dropdown-divider v-if="searchable" />
    <div class="option-list-wrap">
      <overlay-scrollbars :options="scrollBarsOptions" ref="dropselectScrollBar" class="dropselect-scrollbar">
        <b-dropdown-group v-if="options.length > 0" class="option-list">
          <b-dropdown-item
            v-for="item in options"
            :key="item.id"
            class="list-item"
            @click="selectOption({ name: item.name, ...item.value })"
          >
            <font-awesome-icon
              v-if="item.value && item.value.project_id"
              icon="fa-regular fa-arrow-turn-down-right"
              class="project-icon"
            />
            {{ item.name }}
          </b-dropdown-item>
        </b-dropdown-group>
        <b-dropdown-group v-else class="option-list">
          <b-dropdown-item
            v-if="searchable && allowAdd"
            @click="
              () => {
                $emit('add-customer', filters.search_by_name)
              }
            "
            class="list-item"
          >
            <font-awesome-icon icon="fa-solid fa-plus" class="project-icon text-primary" />
            <span class="text-primary pr-1">{{ $gettext('Add') }}</span> <span>{{ filters.search_by_name }}</span>
          </b-dropdown-item>
          <b-dropdown-item v-else class="list-item">{{ $gettext('No results found') }}</b-dropdown-item>
        </b-dropdown-group>
      </overlay-scrollbars>
    </div>
  </b-dropdown>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from '@/utils/'
import CustomerService from '@/store/services/customer.service.js'
import GekkoButton from '@/components/GekkoButton'
export default {
  name: 'CustomerSelect',
  components: { GekkoButton },
  props: {
    withProjects: {
      type: Boolean,
      default: true,
    },
    hideArchivedProjects: {
      type: Boolean,
      default: false,
    },
    preselect: {
      type: Object,
      default: () => {},
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    exclude: {
      type: Array,
      default: () => [],
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    allowAdd: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    toggleVariant: {
      type: String,
      default: '',
    },
    allowAddBtn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      next: null,
      debounceTime: 500,
      options: [],
      customerList: [],
      selected: {
        name: '',
        customer_id: null,
        project_id: null,
      },
      filters: {
        is_archived: 'False',
        is_customer: 'True',
        extended: 'True',
        search_by_name: '',
      },
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
        callbacks: {
          onScroll: this.handleScroll,
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
    }),
    toggleVariantClass() {
      return this.toggleVariant ? 'toggle-' + this.toggleVariant : ''
    },
  },
  created() {
    this.init()

    this.debouncedSearch = debounce(() => {
      this.getPaginatedResults()
    }, this.debounceTime)

    this.debouncedNextPage = debounce(() => {
      this.getNextPageResults()
    }, this.debounceTime)
  },
  mounted() {
    this.osInstance = this.$refs.dropselectScrollBar.osInstance()
  },
  methods: {
    init() {
      this.selected.name = this.withProjects
        ? this.$gettext('Select customer or project')
        : this.$gettext('Select customer')
      this.filters.owner_company = this.currentCompany.id
      this.getPaginatedResults().then(() => {
        if (this.preselect) {
          this.selectOption(this.preselect, true)
        }
      })
    },
    getPaginatedResults() {
      return CustomerService.listPaginated(this.filters).then((response) => {
        this.parseResults(response.data.results, response.data.next, true)
      })
    },
    getNextPageResults() {
      return CustomerService.listNextPage(this.next).then((response) => {
        this.parseResults(response.data.results, response.data.next)
      })
    },
    parseResults(results, next = null, reset = false) {
      this.customerList = results
      this.next = next
      if (reset) this.options.splice(0)

      this.customerList.forEach(customer => {
        // include only customers and or projects not present in this.exclude
        if (this.exclude.length < 1 || !this.exclude.includes(customer.id)) {
          const option_value = { customer_id: customer.id }
          const option_name = customer.name
          this.options.push({ value: option_value, name: option_name })
          if (this.withProjects && customer.projects && customer.projects.length > 0) {
            customer.projects.forEach(project => {
              if (project.is_archived && this.hideArchivedProjects) {
                return
              }
              const option_value = { customer_id: project.company, project_id: project.id }
              const option_name = project.name
              this.options.push({ value: option_value, name: option_name })
            })
          }
        }
      })
      if (this.allowEmpty) {
        this.options.unshift({ value: 0, name: this.$gettext('No Customer') })
      }
    },
    selectOption(selected_item, initial = false) {
      this.selected = selected_item
      if (selected_item.customer_id) {
        if (initial) {
          this.$emit('pre-selected-customer', this.selected)
        } else {
          this.$emit('selected-customer', this.selected)
        }
      }
    },
    clearSearch() {
      this.filters.search_by_name = ''
      this.debouncedSearch()
      this.$refs.customerdropdown.show()
    },
    handleScroll(e) {
      const scrollEndThreshold = 0.9
      if (this.osInstance.scroll().ratio.y >= scrollEndThreshold && this.next) {
        this.debouncedNextPage()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
</style>